/**
 * Funciones puras para el cálculo de puntuaciones de la NOM-035
 */

/**
 * Tipos de respuestas posibles para preguntas
 */
export type QuestionAnswer = string | number | boolean | null;

/**
 * Tipo para la escala de valores
 */
export type ValueScale = { [key: string]: number };

/**
 * Calcula la puntuación para una pregunta de tipo escala
 * @param answer Respuesta dada a la pregunta
 * @param scale Escala de valores para las respuestas
 * @param isInverted Si la pregunta tiene valores invertidos
 */
export function calculateScaleQuestionScore(
  answer: QuestionAnswer,
  scale: ValueScale,
  isInverted: boolean
): number {
  // Asegurarnos de que el valor sea un número
  let value: number;
  if (typeof answer === 'string') {
    value = parseInt(answer, 10);
  } else if (typeof answer === 'number') {
    value = answer;
  } else {
    console.error(`Invalid answer type:`, typeof answer, answer);
    return 0; // Valor por defecto si no es válido
  }
  
  // Verificar que el valor no sea NaN
  if (isNaN(value)) {
    console.error(`NaN value detected:`, answer);
    return 0;
  }
  
  // Verificar que el valor esté dentro del rango de la escala
  const scaleValues = Object.values(scale);
  if (!scaleValues.includes(value)) {
    // Solo advertir si el valor no es 0 (valor por defecto/no seleccionado)
    if (value !== 0) {
      console.warn(`Value ${value} is not in scale:`, scaleValues);
    }
    // Intentar encontrar el valor más cercano en la escala
    if (scaleValues.length > 0) {
      const closestValue = scaleValues.reduce((prev, curr) => 
        Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
      );
      value = closestValue;
    } else {
      // Si la escala está vacía, usar 0 por defecto
      value = 0;
    }
  }
  
  // Para preguntas invertidas, invertir el valor de la escala
  if (isInverted) {
    // Encontrar el valor máximo en la escala (normalmente 4)
    const maxValue = Math.max(...Object.values(scale));
    // Encontrar el valor mínimo en la escala (normalmente 0)
    const minValue = Math.min(...Object.values(scale));
    // Invertir el valor
    value = maxValue - (value - minValue);
  }
  
  return value;
}

/**
 * Calcula la puntuación para una pregunta de tipo booleano
 * @param answer Respuesta dada a la pregunta (true/false)
 * @param isInverted Si la pregunta tiene valores invertidos
 */
export function calculateBooleanQuestionScore(
  answer: QuestionAnswer,
  isInverted: boolean
): number {
  // Convertir la respuesta a booleano
  let boolValue: boolean;
  if (typeof answer === 'string') {
    boolValue = answer.toLowerCase() === 'true';
  } else if (typeof answer === 'boolean') {
    boolValue = answer;
  } else if (typeof answer === 'number') {
    boolValue = answer !== 0;
  } else {
    console.error(`Invalid boolean answer type:`, typeof answer, answer);
    return 0;
  }
  
  // Para preguntas invertidas, invertir el valor
  if (isInverted) {
    boolValue = !boolValue;
  }
  
  return boolValue ? 1 : 0;
}

/**
 * Tipos de preguntas soportados
 */
export type QuestionType = 'scale' | 'boolean' | string;

/**
 * Calcula la puntuación para cualquier tipo de pregunta
 * @param questionType Tipo de pregunta ('scale', 'boolean', etc.)
 * @param answer Respuesta dada a la pregunta
 * @param scale Escala de valores para las respuestas (solo para preguntas de escala)
 * @param isInverted Si la pregunta tiene valores invertidos
 */
export function calculateQuestionScore(
  questionType: QuestionType,
  answer: QuestionAnswer,
  scale: ValueScale | undefined,
  isInverted: boolean
): number {
  // Manejar respuestas nulas o indefinidas
  if (answer === null || answer === undefined) {
    return 0;
  }
  
  // Manejar respuestas de tipo string que contienen 'scale'
  if (typeof questionType === 'string' && questionType.toLowerCase().includes('scale')) {
    // Si el tipo de pregunta es 'scale' pero no tenemos una escala, crear una por defecto
    if (!scale || Object.keys(scale).length === 0) {
      // Crear una escala por defecto basada en la respuesta si es un número
      if (typeof answer === 'number') {
        // Usar el valor de la respuesta directamente
        return isInverted ? 4 - answer : answer;
      }
      // Si la respuesta es una cadena que puede ser parseada como un número
      if (typeof answer === 'string' && !isNaN(parseInt(answer, 10))) {
        const numValue = parseInt(answer, 10);
        return isInverted ? 4 - numValue : numValue;
      }
      // Si no podemos determinar un valor, devolver 0
      return 0;
    }
    return calculateScaleQuestionScore(answer, scale, isInverted);
  } else if (questionType === 'boolean' || typeof answer === 'boolean') {
    return calculateBooleanQuestionScore(answer, isInverted);
  }
  
  // Para otros tipos de preguntas, intentar manejarlos de manera inteligente
  if (typeof answer === 'number') {
    return isInverted ? 4 - answer : answer;
  }
  if (typeof answer === 'string' && !isNaN(parseInt(answer, 10))) {
    const numValue = parseInt(answer, 10);
    return isInverted ? 4 - numValue : numValue;
  }
  
  // Mostrar advertencia pero no saturar la consola
  if (questionType !== 'scale') {
    console.warn(`Unsupported question type or missing scale: ${questionType}`);
  }
  return 0;
}
